import type { CaptureResult, Properties } from 'posthog-js'
import type { LogsEvent } from '@datadog/browser-logs'
import type { Breadcrumb, Event, ReplayFrameEvent } from '@sentry/react'

/** What a credential query value reads as once it has been scrubbed. */
const REDACTED = '[redacted]'

// An edit link carries its credential in `token` and its role in `access`
// (`/auth/<orderId>?token=…&access=…`). The value runs to the next `&`, `#`,
// whitespace or quote, so a URL inside a longer string is scrubbed too.
const CREDENTIAL_PARAM = /([?&](?:token|access)=)[^&#\s"'<>]*/g

/**
 * Replaces the value of every `token` and `access` query parameter in
 * `value` (a full URL or any text holding one) with `[redacted]`; every other
 * parameter and all other text is returned unchanged. Only what the app
 * sends to PostHog, Datadog and Sentry goes through this, never the URL it
 * reads.
 */
export const scrubTrackedUrl = (value: string): string =>
  value.replace(CREDENTIAL_PARAM, `$1${REDACTED}`)

// Nested values (autocapture's `$elements` with their `attr__href`, a log's
// `http.url` or error stack, a custom property holding an object) are walked
// too, to this depth.
const MAX_DEPTH = 8

const scrubValue = (value: unknown, depth: number): unknown => {
  if (typeof value === 'string') return scrubTrackedUrl(value)
  if (depth >= MAX_DEPTH || typeof value !== 'object' || value === null) {
    return value
  }
  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, depth + 1))
  }
  const scrubbed: Record<string, unknown> = {}
  for (const [key, item] of Object.entries(value)) {
    scrubbed[key] = scrubValue(item, depth + 1)
  }
  return scrubbed
}

// A replay's DOM snapshots are large and are scrubbed only where they record
// the page URL (below), so the generic walk leaves them alone.
const scrubProperties = (properties: Properties | undefined) => {
  if (!properties) return properties
  const scrubbed: Properties = {}
  for (const [key, value] of Object.entries(properties)) {
    scrubbed[key] = key === '$snapshot_data' ? value : scrubValue(value, 0)
  }
  return scrubbed
}

// rrweb's meta event (type 4) records the page URL in `data.href`; it is the
// URL a session replay shows.
const RRWEB_META_EVENT = 4

const scrubSnapshotData = (snapshots: unknown) => {
  if (!Array.isArray(snapshots)) return snapshots
  return snapshots.map((snapshot: unknown) => {
    if (
      typeof snapshot !== 'object' ||
      snapshot === null ||
      (snapshot as { type?: unknown }).type !== RRWEB_META_EVENT
    ) {
      return snapshot
    }
    const meta = snapshot as { data?: { href?: unknown } }
    if (typeof meta.data?.href !== 'string') return snapshot
    return {
      ...meta,
      data: { ...meta.data, href: scrubTrackedUrl(meta.data.href) },
    }
  })
}

/**
 * PostHog `before_send`: scrubs credential parameters from every string in
 * the event's properties, nested ones included (`$current_url`, `$referrer`,
 * the `$initial_*` and `$session_entry_*` copies, the custom `page_url` and
 * `referrer`, autocapture's `$elements`), from its `$set` and `$set_once`
 * person properties, and from the page URL a session replay's meta event
 * records.
 */
export const scrubCaptureResult = (
  event: CaptureResult | null,
): CaptureResult | null => {
  if (!event) return event
  const properties = scrubProperties(event.properties) ?? {}
  if (event.event === '$snapshot' && '$snapshot_data' in properties) {
    properties.$snapshot_data = scrubSnapshotData(properties.$snapshot_data)
  }
  return {
    ...event,
    properties,
    ...(event.$set ? { $set: scrubProperties(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: scrubProperties(event.$set_once) } : {}),
  }
}

/**
 * Datadog browser logs `beforeSend`: scrubs every string of the log in place,
 * the page URL and referrer Datadog attaches (`view`), `message`, `http.url`,
 * the error stack and any custom context. Returns true so the log is still
 * sent.
 */
export const scrubLogsEvent = (event: LogsEvent): boolean => {
  const scrubbed = scrubValue(event, 0) as Record<string, unknown>
  for (const key of Object.keys(scrubbed)) {
    ;(event as Record<string, unknown>)[key] = scrubbed[key]
  }
  return true
}

// A query string on its own (`token=…&lang=fr`) has no `?` before its first
// parameter, so it is scrubbed as if it had one.
const scrubQueryString = (value: string): string =>
  scrubTrackedUrl(`?${value}`).slice(1)

const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => ({
  ...breadcrumb,
  ...(typeof breadcrumb.message === 'string'
    ? { message: scrubTrackedUrl(breadcrumb.message) }
    : {}),
  ...(breadcrumb.data
    ? { data: scrubValue(breadcrumb.data, 0) as Breadcrumb['data'] }
    : {}),
})

const scrubRequest = (
  request: NonNullable<Event['request']>,
): NonNullable<Event['request']> => ({
  ...request,
  ...(typeof request.url === 'string'
    ? { url: scrubTrackedUrl(request.url) }
    : {}),
  ...(typeof request.query_string === 'string'
    ? { query_string: scrubQueryString(request.query_string) }
    : {}),
  ...(request.headers
    ? { headers: scrubValue(request.headers, 0) as Record<string, string> }
    : {}),
  ...(request.data !== undefined ? { data: scrubValue(request.data, 0) } : {}),
})

/**
 * Sentry `beforeSend` and `beforeSendTransaction`: scrubs credential
 * parameters from every field of an event that records a URL: the request
 * (its URL, query string, headers, the `Referer` among them, and data), every
 * breadcrumb's message and data (navigation `from` and `to`, fetch and XHR
 * URLs), the transaction name, the tags, a transaction's spans (description
 * and data) and its trace context's data. The exception, the message and the
 * fingerprint are left as they are, so an error groups as it did before.
 */
export const scrubSentryEvent = <T extends Event>(event: T): T => {
  const traceData = event.contexts?.trace?.data
  return {
    ...event,
    ...(event.request ? { request: scrubRequest(event.request) } : {}),
    ...(event.breadcrumbs
      ? { breadcrumbs: event.breadcrumbs.map(scrubBreadcrumb) }
      : {}),
    ...(typeof event.transaction === 'string'
      ? { transaction: scrubTrackedUrl(event.transaction) }
      : {}),
    ...(event.tags
      ? { tags: scrubValue(event.tags, 0) as NonNullable<Event['tags']> }
      : {}),
    ...(event.spans
      ? {
          spans: event.spans.map((span) => ({
            ...span,
            ...(typeof span.description === 'string'
              ? { description: scrubTrackedUrl(span.description) }
              : {}),
            ...(span.data
              ? { data: scrubValue(span.data, 0) as typeof span.data }
              : {}),
          })),
        }
      : {}),
    ...(traceData && event.contexts?.trace
      ? {
          contexts: {
            ...event.contexts,
            trace: {
              ...event.contexts.trace,
              data: scrubValue(traceData, 0) as typeof traceData,
            },
          },
        }
      : {}),
  }
}

/**
 * Sentry replay `beforeAddRecordingEvent`: scrubs every string of a replay's
 * breadcrumb and span frames, the navigation breadcrumbs' `from` and `to` and
 * the navigation spans' URLs among them. Sentry hands this hook those frames
 * only, never rrweb's own events.
 */
export const scrubReplayFrameEvent = (
  event: ReplayFrameEvent,
): ReplayFrameEvent =>
  ({
    ...event,
    data: { ...event.data, payload: scrubValue(event.data.payload, 0) },
  }) as ReplayFrameEvent

type ReplayOrFeedbackEvent = Event & {
  urls?: string[]
  contexts?: Event['contexts'] & { feedback?: { url?: unknown } }
}

/**
 * Scrubs the two Sentry events `beforeSend` never sees: a replay's event,
 * which lists the URLs the replay visited (`urls`) and its request, and a
 * feedback report, which records the page URL in `contexts.feedback.url`.
 */
export const scrubReplayAndFeedbackIntegration = () => ({
  name: 'ScrubReplayAndFeedbackUrls',
  processEvent(event: ReplayOrFeedbackEvent) {
    if (event.type !== 'replay_event' && event.type !== 'feedback') {
      return event
    }
    const scrubbed: ReplayOrFeedbackEvent = scrubSentryEvent(event)
    if (scrubbed.urls) scrubbed.urls = scrubbed.urls.map(scrubTrackedUrl)
    const feedback = scrubbed.contexts?.feedback
    if (feedback && typeof feedback.url === 'string') {
      scrubbed.contexts = {
        ...scrubbed.contexts,
        feedback: { ...feedback, url: scrubTrackedUrl(feedback.url) },
      }
    }
    return scrubbed
  },
})
